import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(4)};
  max-width: 400px;
  width: 90%;
  text-align: center;
  animation: ${slideUp} 0.3s ease;
  position: relative;
  border-top: 4px solid ${props => 
    props.success ? props.theme.colors.success : props.theme.colors.error};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.success ? props.theme.colors.success : props.theme.colors.error}22;
  color: ${props => props.success ? props.theme.colors.success : props.theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing(3)};
  font-size: 24px;
`;

const Message = styled.h3`
  margin: 0 0 ${props => props.theme.spacing(3)};
  color: ${props => props.theme.colors.textPrimary};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${props => props.theme.colors.inputGray};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${props => props.theme.spacing(3)};

  &::after {
    content: '';
    display: block;
    width: ${props => (props.progress)}%;
    height: 100%;
    background: ${props => props.success ? props.theme.colors.success : props.theme.colors.error};
    transition: width 0.05s linear;
  }
`;

const CountdownText = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${props => props.theme.spacing(2)};
`;

const FeedbackModal = ({ 
  isOpen, 
  message, 
  success = true,
  duration = 2000,
  onComplete 
}) => {
  const [progress, setProgress] = useState(100);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(duration / 1000));

  useEffect(() => {
    if (!isOpen) return;

    const progressInterval = 50; // Update progress every 50ms for smooth animation
    const startTime = Date.now();
    const endTime = startTime + duration;

    // Handle progress bar updates
    const progressTimer = setInterval(() => {
      const now = Date.now();
      const remaining = endTime - now;
      
      if (remaining <= 0) {
        clearInterval(progressTimer);
        onComplete();
      } else {
        setProgress((remaining / duration) * 100);
      }
    }, progressInterval);

    // Handle countdown updates (separate from progress for accuracy)
    const countdownTimer = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(countdownTimer);
    };
  }, [isOpen, duration, onComplete]);

  if (!isOpen) return null;

  return (
    <Overlay>
      <ModalContent success={success}>
        <IconWrapper success={success}>
          {success ? <FaCheck /> : <FaTimes />}
        </IconWrapper>
        <Message>{message}</Message>
        <ProgressBar progress={progress} success={success} />
        <CountdownText>
          Redirecting in {secondsLeft} seconds...
        </CountdownText>
      </ModalContent>
    </Overlay>
  );
};

export default FeedbackModal;